<template>
  <b-modal
    id="modal-receive-order"
    centered
    size="lg"
    title="收貨"
    hide-footer
    @hide="resetModal"
  >
    <b-overlay
      :show="loading"
      spinner-variant="primary"
      spinner-type="grow"
      spinner-small
      rounded="sm"
    >
      <validation-observer #default="{ handleSubmit }" ref="refFormObserver">
        <b-form @submit.prevent="handleSubmit(onSubmit)">
          <b-row>
            <!-- 入貨單號 -->
            <b-col cols="12">
              <b-form-group label="入貨單號" label-for="po_number">
                <b-form-input
                  id="po_number"
                  v-model="orderData.po_number"
                  disabled
                />
              </b-form-group>
            </b-col>

            <!-- 收貨日期 -->
            <b-col cols="12">
              <validation-provider
                #default="validationContext"
                name="收貨日期"
                rules="required"
              >
                <b-form-group label="收貨日期" label-for="receipt_date">
                  <flat-pickr
                    v-model="receiveData.receipt_date"
                    class="form-control"
                    :config="{ dateFormat: 'Y-m-d' }"
                    placeholder="YYYY-MM-DD"
                  />
                  <b-form-invalid-feedback
                    :state="getValidationState(validationContext)"
                  >
                    {{ validationContext.errors[0] }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>
            </b-col>

            <!-- 備註 -->
            <b-col cols="12">
              <b-form-group label="備註" label-for="remark">
                <b-form-textarea
                  id="remark"
                  v-model="receiveData.remark"
                  placeholder="備註"
                  rows="3"
                />
              </b-form-group>
            </b-col>

            <!-- 產品列表 -->
            <b-col cols="12">
              <div class="table-responsive">
                <table class="table">
                  <thead>
                    <tr>
                      <th>產品名稱</th>
                      <th>訂購數量</th>
                      <th>已收貨數量</th>
                      <th>本次收貨數量</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="item in orderData.items" :key="item.id">
                      <td>{{ item.product.name }}</td>
                      <td>{{ item.quantity }}</td>
                      <td>{{ item.received_quantity }}</td>
                      <td>
                        <b-form-input
                          v-model.number="receiveData.items[item.id]"
                          type="number"
                          :max="item.quantity - item.received_quantity"
                          min="0"
                          @input="validateQuantity($event, item)"
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </b-col>

            <!-- 按鈕 -->
            <b-col cols="12" class="text-center mt-2">
              <b-button variant="primary" class="mr-1" type="submit">
                確認收貨
              </b-button>
              <b-button variant="outline-secondary" @click="hide">
                取消
              </b-button>
            </b-col>
          </b-row>
        </b-form>
      </validation-observer>
    </b-overlay>
  </b-modal>
</template>

<script>
import {
  BModal,
  BForm,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BButton,
  BOverlay,
  BRow,
  BCol,
} from "bootstrap-vue";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import flatPickr from "vue-flatpickr-component";
import formValidation from "@core/comp-functions/forms/form-validation";
import store from "@/store";

export default {
  components: {
    BModal,
    BForm,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BButton,
    BOverlay,
    BRow,
    BCol,
    flatPickr,
    ValidationProvider,
    ValidationObserver,
  },
  props: {
    orderData: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      receiveData: {
        purchase_order_id: 0,
        receipt_date: "",
        remark: "",
        items: {},
      },
    };
  },
  methods: {
    hide() {
      this.$bvModal.hide("modal-receive-order");
    },
    resetModal() {
      this.receiveData = {
        purchase_order_id: 0,
        receipt_date: "",
        remark: "",
        items: {},
      };
      this.$emit("modal-hidden");
    },
    validateQuantity(value, item) {
      const maxQuantity = item.quantity - item.received_quantity;
      if (value > maxQuantity) {
        this.receiveData.items[item.id] = maxQuantity;
      }
    },
    onSubmit() {
      // 準備提交數據
      const submitData = {
        purchase_order_id: this.orderData.id,
        receipt_date: this.receiveData.receipt_date,
        remark: this.receiveData.remark,
        items: Object.entries(this.receiveData.items).map(([id, quantity]) => ({
          purchase_order_item_id: parseInt(id),
          quantity: parseInt(quantity),
        })),
      };

      this.$swal({
        title: "確認收貨?",
        showCancelButton: true,
        confirmButtonText: "確認",
        cancelButtonText: "取消",
        customClass: {
          confirmButton: "btn btn-primary",
          cancelButton: "btn btn-outline-danger ml-1",
        },
        buttonsStyling: false,
      }).then((result) => {
        if (result.value) {
          this.loading = true;
          store
            .dispatch("app-order/receiveOrder", submitData)
            .then((response) => {
              this.loading = false;
              this.$swal({
                text: response.data.message,
                icon: "success",
                customClass: {
                  confirmButton: "btn btn-primary",
                },
                showClass: {
                  popup: "animate__animated animate__bounceIn",
                },
                buttonsStyling: false,
              });
              this.$emit("refetch-data");
              this.hide();
            })
            .catch((error) => {
              this.loading = false;
              this.$swal({
                text: error.response.data.message,
                icon: "error",
                customClass: {
                  confirmButton: "btn btn-primary",
                },
                showClass: {
                  popup: "animate__animated animate__bounceIn",
                },
                buttonsStyling: false,
              });
            });
        }
      });
    },
  },
  created() {
    this.receiveData.purchase_order_id = this.orderData.id;
  },
  setup() {
    const { refFormObserver, getValidationState } = formValidation();
    return {
      refFormObserver,
      getValidationState,
    };
  },
};
</script>
